import { useEffect, useMemo, useRef, useState } from 'react';
import * as v from 'valibot';

import { Button, type ColumnDef, DataTable, Icon, Modal, Pagination, Radio, RadioGroup, Text } from '@nvision/ui';

import { NoData } from '@/modules/errors';

import { useProducts } from '../../../hooks';
import {
  type ProductRow,
  type ProductSearchFormValues,
  type ProductsParams,
  productSearchParamsSchema,
} from '../../../models';
import { ProductSearchForm, type SearchFormRef } from '../product-search-form';

import styles from './product-search-modal.module.css';

interface ProductSearchModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onSelectionChange: (values: ProductRow) => void;
}

export const ProductSearchModal: React.FC<ProductSearchModalProps> = ({
  isOpen = false,
  setIsOpen,
  onSelectionChange,
}) => {
  const [params, setParams] = useState<ProductsParams>(v.getDefaults(productSearchParamsSchema));

  const { data, isLoading } = useProducts(params);

  const products = data?.data;

  const [selectedProduct, setSelectedProduct] = useState<ProductRow | null>();
  const selectedProductRef = useRef<ProductRow | null>(null);

  const searchFormRef = useRef<SearchFormRef>({} as SearchFormRef);

  const reset = () => {
    setSelectedProduct(null);
    setParams(v.getDefaults(productSearchParamsSchema));
  };

  const close = () => {
    searchFormRef.current.reset();
    setSelectedProduct(null);
    setIsOpen(false);
  };

  const submit = (values: ProductSearchFormValues) => {
    setParams((prevParams) => ({
      ...prevParams,
      ...values,
    }));
  };

  useEffect(() => {
    selectedProductRef.current = selectedProduct || null;
  }, [selectedProduct]);

  const tableColumns: ColumnDef<ProductRow, any>[] = useMemo(
    () => [
      {
        // TODO: fix sizing
        accessorKey: 'id',
        cell: (props) => {
          const row = props.row.original;
          const isSelected = selectedProductRef.current?.productCode === row.productCode;

          return (
            <RadioGroup
              value={isSelected ? String(row.productCode) : ''}
              className={styles.radio}
              onChange={() => {
                setSelectedProduct(row);
                selectedProductRef.current = row;
              }}
            >
              <Radio value={String(props.row.original.productCode)} className={styles.radio} />
            </RadioGroup>
          );
        },
        header: '',
        maxSize: 0,
      },
      {
        accessorKey: 'productCode',
        cell: (props) => props.row.original.productCode,
        header: 'Product Code',
      },
      {
        accessorKey: 'productDesc',
        cell: (props) => props.row.original.productDesc,
        header: 'Product Description',
      },
    ],
    [],
  );

  return (
    <Modal isOpen={isOpen} onOpenChange={(value) => setIsOpen(value)} className={styles.modal}>
      <Modal.Header className={styles.header}>
        <Text size="xlg" weight="semibold">
          Product Search
        </Text>
        <Icon name="x" onClick={close} cursor="pointer" />
      </Modal.Header>
      <Modal.Content className={styles.content}>
        <ProductSearchForm formRef={searchFormRef} handleSubmit={submit} handleReset={reset} />
        {products ? (
          <div>
            <div className={styles.total}>
              <Text weight="bold">{`${data?.total} products `}</Text>are found
            </div>
            <div className={styles.tableContainer}>
              <DataTable
                columns={tableColumns}
                data={products}
                isLoading={isLoading}
                page={params.offset + 1}
                limit={params.limit}
                totalItems={data?.total}
              />
            </div>

            <Pagination
              className={styles.pagination}
              totalItems={data?.total || 0}
              limit={params.limit}
              page={params.offset / params.limit + 1}
              onPageChange={(page: number) => {
                setParams((prev) => ({ ...prev, offset: (page - 1) * params.limit }));
              }}
              onOffsetChange={(offset: number) => setParams({ ...params, offset })}
              onLimitChange={(limit: number) => setParams({ ...params, limit, offset: 0 })}
            />
          </div>
        ) : (
          <NoData
            className={styles.noData}
            description="No data to display. Please select the product code or description above."
          />
        )}
      </Modal.Content>
      <Modal.Footer className={styles.footer}>
        <Button outline secondary onClick={close}>
          Cancel
        </Button>
        <Button isDisabled={!selectedProduct} onClick={() => onSelectionChange(selectedProduct as ProductRow)}>
          Submit
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
